import { useMemo } from "react";

import { templatePaginatedSearchResultsRelayRefetchableFragment$key } from "@/__generated__/templatePaginatedSearchResultsRelayRefetchableFragment.graphql";

export type CategorySearchSuggestion = {
    type: "category";
    id: string;
    name?: string | null;
    description?: string | null;
};

import {
    CategoryKey,
    CategoryTemplateOrder
} from "../../config/category-template-order";
import { useQueryLocale } from "../hooks/use-query-locale";

import {
    parseSearchQuery,
    TemplateInitialSearchResults
} from "./template-initial-search-results-relay";
import { TemplatePaginatedSearchResults } from "./template-paginated-search-results-relay";

export type TemplateCategorySearchResultsProps = {
    preloadedSearchResults?: templatePaginatedSearchResultsRelayRefetchableFragment$key | null;
    searchTerm: CategorySearchSuggestion;
    /**
     * A unique key to identify the search term.
     * This is required to force a new instance
     * of `TemplatePaginatedSearchResults`
     * when the search term changes.
     */
    searchKey: string;
};

export const TemplateCategorySearchResults = ({
    preloadedSearchResults,
    searchTerm,
    searchKey
}: TemplateCategorySearchResultsProps) => {
    const locale = useQueryLocale();
    const searchQuery = useMemo(
        () => parseSearchQuery(searchTerm, { withLocales: [locale] }),
        [searchTerm, locale]
    );

    const sortLookup =
        CategoryTemplateOrder[searchTerm.id as CategoryKey] ?? "name";

    return preloadedSearchResults ? (
        <TemplatePaginatedSearchResults
            key={searchKey}
            initialSearchResults={preloadedSearchResults}
            searchQuery={searchQuery}
            sortLookup={sortLookup}
            wasPreloaded={true}
        />
    ) : (
        <TemplateInitialSearchResults
            searchKey={searchKey}
            searchQuery={searchQuery}
            sortLookup={sortLookup}
        />
    );
};
